import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { UserService } from '../../shared/services/user.service';
import { SpaceService } from '../../shared/services/space.service';
import { GlobalAlertService } from '../../shared/services/global-alert.service';

export interface ChangePrimaryOrgData {
  userId: string;
  currentOrgId: string;
  currentOrgName: string;
}

interface OrgOption {
  id: string;
  display_label: string;
  isCurrent: boolean;
}

@Component({
  selector: 'app-change-primary-org-dialog',
  templateUrl: './change-primary-org-dialog.component.html',
  styleUrls: ['./change-primary-org-dialog.component.scss'],
})
export class ChangePrimaryOrgDialogComponent implements OnInit, OnDestroy {
  dialogState = 'DEFAULT';
  updateRoles = false;
  orgControl = new FormControl('');
  orgs: OrgOption[] = [];
  loadingOrgs = true;
  filteredOrgs$: Observable<OrgOption[]>;
  selectedOrg: OrgOption | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<ChangePrimaryOrgDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangePrimaryOrgData,
    private userService: UserService,
    private spaceService: SpaceService,
    private messageService: GlobalAlertService,
    private translate: TranslateService,
  ) {
    this.dialogRef.backdropClick().subscribe(() => this.onCancel());
  }

  ngOnInit(): void {
    // Source the selectable orgs from the SAME list shown in the global-nav
    // org selector (SpaceService.contextList$). The current primary org is
    // included but flagged so the UI marks it and disables selecting it.
    const sub = this.spaceService.contextList$.subscribe((spaces) => {
      this.orgs = this.collectOrgs(spaces || []);
      if (this.orgs.length > 0) {
        this.loadingOrgs = false;
      }
    });
    this.subscriptions.push(sub);
    // The nav populates contextList$ at startup; load it defensively (with a
    // loader) in case the dialog is reached before that happened.
    if (!this.spaceService.contextList$.value || this.spaceService.contextList$.value.length === 0) {
      this.spaceService
        .getContextList()
        .catch((err) => console.error('Could not load org list', err))
        .finally(() => (this.loadingOrgs = false));
    } else {
      this.loadingOrgs = false;
    }

    this.filteredOrgs$ = this.orgControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => (typeof value === 'string' ? value : value?.display_label || '')),
      map((name: string) => this.filterOrgs(name)),
    );
  }

  private collectOrgs(spaces: any[]): OrgOption[] {
    // Mirror exactly the orgs the global-nav selector shows (the context list),
    // walking children for multi-level contexts. The current primary org is
    // kept in the list but flagged `isCurrent` (the template marks it bold +
    // check and disables selecting it).
    const result: OrgOption[] = [];
    const seen = new Set<string>();
    const visit = (nodes: any[] = []) => {
      for (const node of nodes) {
        if (!node || !node.id || seen.has(node.id)) {
          continue;
        }
        seen.add(node.id);
        result.push({
          id: node.id,
          display_label: node.display_label || node.id,
          isCurrent: node.id === this.data.currentOrgId,
        });
        if (Array.isArray(node.children) && node.children.length) {
          visit(node.children);
        }
      }
    };
    visit(spaces);
    // Current org first (orientation), then alphabetical.
    return result.sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) {
        return a.isCurrent ? -1 : 1;
      }
      return a.display_label.localeCompare(b.display_label);
    });
  }

  private filterOrgs(name: string): OrgOption[] {
    const filter = (name || '').toLowerCase();
    return this.orgs.filter((o) => o.display_label.toLowerCase().includes(filter));
  }

  displayOrg(org?: OrgOption): string {
    return org ? org.display_label : '';
  }

  onOrgSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOrg = event.option.value as OrgOption;
  }

  onCancel(): void {
    if (this.dialogState === 'DEFAULT') {
      this.dialogRef.close(false);
    }
  }

  async confirm(): Promise<void> {
    if (!this.selectedOrg) {
      return;
    }
    const selected = this.selectedOrg;
    this.dialogState = 'UPDATING';
    try {
      await this.userService.changePrimaryOrg(this.data.userId, selected.id, this.updateRoles);
      this.dialogState = 'SUCCESS';
      this.messageService.setSuccess(this.translate.instant('user.dialog.change_primary_org.success'));
      // The moved user no longer belongs to the current org, so it drops off
      // the list; refresh the viewed user so the View shows the new primary org.
      // The user is intentionally NOT redirected to the new org.
      await this.userService.refetchUsers();
      await this.userService.getUser(this.data.userId);
      await new Promise((resolve) => setTimeout(resolve, 1200));
      // Return the new org so the edit page can sync its form — otherwise
      // exiting edit via Save re-submits the stale (old) org and reverts it.
      this.dialogRef.close({ changed: true, newOrgId: selected.id, newOrgName: selected.display_label });
    } catch (err) {
      // The backend tags failures with the phase that failed so we can show
      // the AC-specific message ("error updating roles" vs "...the org").
      const phase = (err && (err as any).error && (err as any).error.phase) || 'organization';
      this.dialogState = phase === 'roles' ? 'FAILED_ROLES' : 'FAILED_ORG';
      const key =
        phase === 'roles'
          ? 'user.dialog.change_primary_org.error_roles'
          : 'user.dialog.change_primary_org.error_org';
      this.messageService.setError(this.translate.instant(key));
      await new Promise((resolve) => setTimeout(resolve, 2500));
      this.dialogRef.close(false);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
